import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import { ArrowRightIcon, CameraIcon } from 'lucide-react';
import { BrandEmblem } from './BrandEmblem';
import { brand } from '../data/brand';
const EASE = [0.23, 1, 0.32, 1] as const;
export function Hero() {
  const ref = useRef<HTMLElement>(null);
  const reduce = useReducedMotion();
  const {
    scrollYProgress
  } = useScroll({
    target: ref,
    offset: ['start start', 'end start']
  });
  const imageY = useTransform(scrollYProgress, [0, 1], ['0%', reduce ? '0%' : '18%']);
  const imageScale = useTransform(scrollYProgress, [0, 1], [1.04, reduce ? 1.04 : 1.14]);
  const contentY = useTransform(scrollYProgress, [0, 1], ['0%', reduce ? '0%' : '-12%']);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
  return <section ref={ref} className="relative flex min-h-[88vh] w-full items-end overflow-hidden bg-forest-800 lg:min-h-screen" data-id="element-327" data-mp="3cz5ug9c21vza">
      <motion.img src="https://cdn.magicpatterns.com/patterns/generated-images/3fe92389-cc96-42ce-8cf1-c9de13180b6b.jpg" alt="Bel Air Luxury Apartment at dusk, with the lit pool in front of the building" style={{
      y: imageY,
      scale: imageScale
    }} className="absolute inset-0 h-full w-full object-cover" fetchPriority="high" decoding="async" data-id="element-328" data-mp="3j7zf9iqmlsgo" />
      <div className="absolute inset-0 bg-forest-800/60" aria-hidden="true" data-id="element-329" data-mp="30iaz4vrfogld" />
      <div className="absolute inset-x-0 bottom-0 h-2/3 bg-[linear-gradient(to_top,rgba(5,28,23,0.88),transparent)]" aria-hidden="true" data-id="element-330" data-mp="30g6jxho33v5s" />

      <motion.div style={{
      y: contentY,
      opacity: contentOpacity
    }} className="relative mx-auto w-full max-w-[1240px] px-5 pb-16 pt-32 sm:px-8 lg:px-12 lg:pb-24" data-id="element-331" data-mp="2ql68aimn7wy8">
        <BrandEmblem animate primary="#F7F4ED" accent="#C6A15B" className="h-16 w-auto sm:h-20" data-id="element-332" data-mp="2hk8685vrlzia" />

        <motion.p initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        ease: EASE,
        delay: 0.35
      }} className="mt-8 flex items-center gap-3 text-[11px] uppercase tracking-wordmark text-gold-300" data-id="element-333" data-mp="2ljpcv2mxh2p1">
          <span className="h-px w-8 bg-gold-400" aria-hidden="true" data-id="element-334" data-mp="n9i4pev29iv6" />
          BEL AIR LUXURY APARTMENT · BENIN CITY
        </motion.p>

        <motion.h1 initial={{
        opacity: 0,
        y: 18
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        ease: EASE,
        delay: 0.42
      }} className="mt-5 max-w-4xl font-display text-[2.4rem] leading-[1.08] text-ivory-100 sm:text-6xl lg:text-[4.6rem]" data-id="element-335" data-mp="ddww2xvhlkcg">
          Redefined Luxury.
          <br data-id="element-336" data-mp="11150ldl3cpdf" />
          <span className="text-gold-300" data-id="element-337" data-mp="110wr7vo6g26z">Timeless Living.</span>
        </motion.h1>

        <motion.div initial={{
        opacity: 0,
        y: 18
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        ease: EASE,
        delay: 0.5
      }} className="mt-8 flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between" data-id="element-338" data-mp="x0l3ub4fjnrt">
          <p className="max-w-md text-base leading-relaxed text-ivory-200/85" data-id="element-339" data-mp="1xa447fpd2mk5">{brand.promise}</p>

          <div className="flex flex-wrap items-center gap-3" data-id="element-340" data-mp="21a4tjve00i5z">
            <Link to="/booking" className="group inline-flex items-center gap-2 rounded-full bg-gold-400 px-7 py-3.5 text-sm text-forest-800 transition-colors duration-150 ease-premium hover:bg-gold-300" data-id="element-341" data-mp="218k0693f759p">
              Book your stay
              <ArrowRightIcon size={16} className="transition-transform duration-200 ease-premium group-hover:translate-x-1" aria-hidden="true" data-id="element-342" data-mp="2fzj4h7x837nh" />
            </Link>
            <Link to="/booking?type=photoshoot" className="inline-flex items-center gap-2 rounded-full border border-ivory-200/40 px-7 py-3.5 text-sm text-ivory-100 transition-colors duration-150 ease-premium hover:bg-ivory-100/10" data-id="element-343" data-mp="2tzlfca7450t8">
              <CameraIcon size={16} aria-hidden="true" data-id="element-344" data-mp="zlstriblll4v" />
              Book a photoshoot
            </Link>
          </div>
        </motion.div>
      </motion.div>
    </section>;
}